import { useMerchant } from '@/src/context/merchant/merchant-context'
import { useMessage } from '@/src/context/message/message-context'
import { useCurrentProduct } from '@/src/context/product/current-product-context'
import { usePromotion } from '@/src/context/promotion/promotion-context'
import { PromotionHelper } from '@/src/helpers/promotion'
import MerchantBenefits from '@/src/interfaces/benefits/merchant'
import ItemInfos from '@/src/interfaces/catalog/item-infos'
import { ItemService } from '@/src/services/catalog/item-service'
import { useEffect, useState } from 'react'
import LoadingProduct from '../_loading-product/loading-product'
import { useAppAccessToken } from '@/src/context/app-access-token/app-access-token-context'

interface Props {
    id: string
    children: React.ReactNode
    setHasLoadedContent?(e: boolean): void
    setHasError(e: boolean): void
}

export default function ProductContent(props: Props) {
    const { setProductDetails, currentProduct } = useCurrentProduct()
    const { showMessage } = useMessage()
    const { merchantSummary, localMode } = useMerchant()
    const { promotion } = usePromotion()
    const { appAccessToken } = useAppAccessToken()

    const [isLoading, setIsLoading] = useState(true)

    const applyPromotionToProduct = (product: ItemInfos.DetailedResponse, promotion?: MerchantBenefits.PromotionResponse) => {
        const promotionProduct = PromotionHelper.getProduct(promotion, product.id)

        if (promotionProduct) {
            product.originalPrice = product.salePrice
            product.salePrice = PromotionHelper.getFromToPrice(promotion, product).to
            product.promoId = PromotionHelper.getPromotionByProductId(promotion, product.id)?.id
            product.promoPriceType = PromotionHelper.getProduct(promotion, product.id)?.valueType
        }
    }

    const applyPromotionToSizes = (sizes: ItemInfos.Size[], promotion?: MerchantBenefits.PromotionResponse) => {
        sizes && sizes.forEach(size => {
            const promotionSizeProduct = PromotionHelper.getProduct(promotion, size.itemId)

            if (promotionSizeProduct) {
                size.originalPrice = size.salePrice
                size.salePrice = PromotionHelper.getFromToPrice(promotion, { salePrice: size.salePrice, id: size.itemId }).to
                size.promoId = PromotionHelper.getPromotionByProductId(promotion, size.itemId)?.id
                size.promoPriceType = PromotionHelper.getProduct(promotion, size.itemId)?.valueType
            }
        })
    }

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                if (!merchantSummary?.id) return

                setIsLoading(true)
                props.setHasLoadedContent && props.setHasLoadedContent(false)

                const params: ItemInfos.DetailedSearchParams = {
                    id: props.id,
                    merchantId: merchantSummary.id,
                    sellLocal: !!localMode
                }

                const response = await ItemService.getDetailed(params, appAccessToken)

                if (response.status === 404) {
                    props.setHasError(true)
                    return showMessage('Produto não encontrado.')
                }

                if (!response.success || !response.model)
                    throw Error(response.errorMessage)

                applyPromotionToProduct(response.model, promotion)
                applyPromotionToSizes(response.model.sizes, promotion)

                setProductDetails(response.model)
            }
            catch (e) {
                const error = e as Error
                props.setHasError(true)
                showMessage("Houve um erro ao carregar este produto.", { type: 'danger', error: error, emptyMessagesOnRouteChange: false, keepOnScreen: true })
            }
            finally {
                setIsLoading(false)
                props.setHasLoadedContent && props.setHasLoadedContent(true)
            }
        }

        if (!currentProduct && localMode !== undefined)
            fetchProduct()
    }, [props.id, merchantSummary, localMode])

    if (isLoading)
        return <LoadingProduct />

    return props.children
}